import { useMemo } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { ETAPES, ETAPE_INFO, calculerScore, membresPresents, type AffectationHote, type Etape, type Groupe } from '@dinertournant/core';
import { BandeauAlerte, PastilleEtape } from '../../components/index.js';
import { useChargement } from '../../hooks/useChargement.js';
import { api, capitale, dateLongue, heureCourte, nomComplet, plageEtape, type FoyerOrganisateur } from '../../lib/api.js';
import type { ContexteEvenement } from './EvenementLayout.js';

interface DonneesRepartition {
  foyers: FoyerOrganisateur[];
  hotes: AffectationHote[];
  groupes: Groupe[];
}

const ARTICLE: Record<Etape, string> = { entree: 'une entrée', plat: 'un plat', dessert: 'un dessert' };

/** Écran 6 (non dessiné) : planning imprimable par étape et fiches hôtes (@media print). */
export function Planning() {
  const { detail } = useOutletContext<ContexteEvenement>();
  const eventId = detail.event.id;
  const { donnees, erreur } = useChargement(() => api.get<DonneesRepartition>(`/api/events/${eventId}/repartition`), [eventId]);
  const foyers = donnees?.foyers ?? [];
  const groupes = donnees?.groupes ?? [];
  const parId = useMemo(() => new Map(foyers.map((f) => [f.id, f])), [foyers]);
  const score = useMemo(() => (groupes.length ? calculerScore(foyers, groupes) : null), [foyers, groupes]);
  const horaires = { entree: detail.event.horaireEntree, plat: detail.event.horairePlat, dessert: detail.event.horaireDessert };
  const participants = foyers.reduce((s, f) => s + membresPresents(f).length, 0);

  if (erreur) return <BandeauAlerte niveau="attention" role="alert" titre={erreur} />;

  return (
    <div className="page-app planning">
      <div className="entete-page no-print">
        <div>
          <h1 className="display">Planning</h1>
          <p className="texte-doux">La soirée table par table, puis une fiche par hôte. Imprimez-le ou gardez-le sous la main le jour J.</p>
        </div>
        <div className="ligne" style={{ flexWrap: 'wrap' }}>
          <button type="button" className="btn" onClick={() => window.print()} disabled={!groupes.length}>
            Imprimer
          </button>
          <Link to={`/app/e/${eventId}/diffusion`} className="btn principal" aria-disabled={!groupes.length}>
            Diffuser les parcours →
          </Link>
        </div>
      </div>

      {!groupes.length ? (
        <div className="carte pile" style={{ maxWidth: 640 }}>
          <h2 style={{ fontSize: 18 }}>Pas encore de planning</h2>
          <p className="texte-doux">Le planning se construit à partir de la répartition des convives.</p>
          <Link className="btn principal" to={`/app/e/${eventId}/repartition`} style={{ alignSelf: 'flex-start' }}>
            Aller à la répartition
          </Link>
        </div>
      ) : (
        <>
          {detail.planning.planningObsolete ? (
            <BandeauAlerte niveau="sombre" titre="Le planning n'est plus à jour." actions={<Link className="btn" to={`/app/e/${eventId}/repartition`}>Voir la répartition</Link>}>
              {detail.planning.changements.length} changement{detail.planning.changements.length > 1 ? 's' : ''} depuis la génération.
            </BandeauAlerte>
          ) : null}

          <header className="entete-impression">
            <div className="surtitre">{detail.event.organisation ?? 'Dîner tournant'}</div>
            <h2 className="display" style={{ fontSize: 30 }}>
              {detail.event.nom}
            </h2>
            <p className="texte-doux">
              {capitale(dateLongue(detail.event.date))}
              {detail.event.zone ? ` · ${detail.event.zone}` : ''} · {participants} participants · {groupes.length} tables
              {score ? ` · ${score.rencontresUniques} rencontres uniques sur ${score.rencontresTotales}` : ''}
            </p>
          </header>

          {ETAPES.map((e) => {
            const ge = groupes.filter((g) => g.etape === e);
            return (
              <section key={e} className="section-etape" aria-label={ETAPE_INFO[e].label}>
                <PastilleEtape etape={e} variante="numero" grande detail={`${plageEtape(horaires, e)} · ${ge.length} table${ge.length > 1 ? 's' : ''}`} />
                <div className="tables-planning">
                  {ge.map((g) => {
                    const hote = parId.get(g.hoteFoyerId);
                    const invites = g.foyerIds.filter((id) => id !== g.hoteFoyerId).map((id) => parId.get(id)).filter((f): f is FoyerOrganisateur => !!f);
                    const taille = g.foyerIds.reduce((s, id) => s + (parId.get(id) ? membresPresents(parId.get(id)!).length : 0), 0);
                    return (
                      <article key={g.hoteFoyerId} className={`table-planning etape-${e}`}>
                        <header>
                          <strong>Chez {hote?.nom ?? '?'}</strong>
                          <span>{taille} pers.</span>
                        </header>
                        <div className="texte-doux adresse">
                          {hote?.adresse ?? 'Adresse à compléter'}
                          {hote?.complementAdresse ? `, ${hote.complementAdresse}` : ''}
                        </div>
                        <ul>
                          <li>
                            <strong>{hote ? nomComplet(hote) : '?'}</strong> <span className="texte-muet">(hôte)</span>
                            {hote && membresPresents(hote).some((m) => m.regime) ? <span className="tag-regime">{membresPresents(hote).filter((m) => m.regime).map((m) => m.regime).join(' · ')}</span> : null}
                          </li>
                          {invites.map((f) => (
                            <li key={f.id}>
                              {nomComplet(f)}
                              {membresPresents(f).some((m) => m.regime) ? (
                                <span className="tag-regime">{membresPresents(f).filter((m) => m.regime).map((m) => (membresPresents(f).length > 1 ? `${m.prenomNom.split(/\s+/)[0]} : ${m.regime}` : m.regime)).join(' · ')}</span>
                              ) : null}
                            </li>
                          ))}
                        </ul>
                      </article>
                    );
                  })}
                </div>
              </section>
            );
          })}

          <h2 className="display" style={{ fontSize: 26, marginTop: 12 }}>
            Fiches hôtes
          </h2>
          <p className="texte-doux no-print">Une page par hôte à l'impression : qui vient, à quelle heure, et les régimes à prévoir. À remettre ou à envoyer à chaque hôte.</p>
          {groupes.map((g) => {
            const hote = parId.get(g.hoteFoyerId);
            if (!hote) return null;
            const invites = g.foyerIds.filter((id) => id !== g.hoteFoyerId).map((id) => parId.get(id)).filter((f): f is FoyerOrganisateur => !!f);
            const taille = g.foyerIds.reduce((s, id) => s + (parId.get(id) ? membresPresents(parId.get(id)!).length : 0), 0);
            // Fiche hôte : les convives restent une surprise, seuls leur nombre et leurs régimes (sans nom) sont indiqués.
            const nbConvives = invites.reduce((s, f) => s + membresPresents(f).length, 0);
            const regimes = invites.flatMap((f) => membresPresents(f).filter((m) => m.regime).map((m) => m.regime as string));
            return (
              <article key={`${g.etape}-${g.hoteFoyerId}`} className={`fiche-hote carte etape-${g.etape}`}>
                <div className="bandeau-fiche" />
                <div className="surtitre">
                  Fiche hôte · {ETAPE_INFO[g.etape].label} · {plageEtape(horaires, g.etape)}
                </div>
                <h3 className="display" style={{ fontSize: 24 }}>
                  {nomComplet(hote)}
                </h3>
                <p className="texte-doux">
                  {hote.adresse ?? ''}
                  {hote.complementAdresse ? `, ${hote.complementAdresse}` : ''}
                  {hote.telephone ? ` · ${hote.telephone}` : ''}
                </p>
                <p>
                  Vous recevez {ARTICLE[g.etape]} pour <strong>{taille} personnes</strong>, vous compris : {nbConvives} convive{nbConvives > 1 ? 's' : ''}, surprise jusqu'à leur arrivée vers {heureCourte(horaires[g.etape])}.
                </p>
                <p>
                  <strong>Régimes à prévoir :</strong> {regimes.length ? regimes.join(' · ') : 'aucun signalé'}.
                </p>
                {detail.event.contactNom || detail.event.contactTelephone ? (
                  <p className="texte-muet">
                    Un imprévu ? {detail.event.contactNom ?? "L'organisation"}
                    {detail.event.contactTelephone ? ` · ${detail.event.contactTelephone}` : ''}
                  </p>
                ) : null}
              </article>
            );
          })}
        </>
      )}
    </div>
  );
}
